import { Link } from '@inertiajs/react';
import classNames from 'classnames';
import { ReactNode } from 'react';
import { Check, ChevronRight, Copy, ExternalLink } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { useLocale } from '../../Shared/locale';
import { AButton, Button } from '../../Shared/UI/Button';
import { Card } from '../../Shared/UI/Card';
import { Icon } from '../../Shared/UI/Icon';
import { Input } from '../../Shared/UI/Input';
import { useCopy } from '../../Shared/useCopy';
import { useRouter } from '../router';
import { SharedProps } from '../types';
import Container from '../UI/Container';
import Header from '../UI/Header';
import HeaderTitle from '../UI/HeaderTitle';
import Layout from '../UI/Layout';

export type OnboardingPageProps = SharedProps

export default function OnboardingPage({
  charity,
}: OnboardingPageProps) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const { locale } = useLocale();

  const [copyCreateCampaignUrl, copiedCreateCampaignUrl] = useCopy(charity.create_campaign_url);

  return (
    <>
      <Header>
        <Container size="md">
          <HeaderTitle>
            {t('backend:onboarding_title')}
          </HeaderTitle>
          {t('backend:onboarding_description')}
        </Container>
      </Header>
      <Container size="md">
        <Card panels header footer>
          <div>
            <h3>
              {t('backend:getting_started')}
            </h3>
          </div>
          <div className="space-y-6">
            <div className={classNames('flex space-x-4', charity.can_receive_payments && 'opacity-50')}>
              <Badge>
                1
              </Badge>
              <div>
                <h4>
                  <Link
                    href={routes.mollie_page()}
                    className="text-slate-800"
                  >
                    {t('backend:onboarding_payments_title')}
                    <Icon className="ml-1">
                      {charity.can_receive_payments ? <Check /> : <ChevronRight/>}
                    </Icon>
                  </Link>
                </h4>
                <div className="text-slate-500">
                  {t('backend:onboarding_payments_description')}
                </div>
              </div>
            </div>
            <div className={classNames('flex space-x-4', charity.logo_url && 'opacity-50')}>
              <Badge>
                2
              </Badge>
              <div>
                <h4>
                  <Link
                    href={routes.charity_settings_page()}
                    className="text-slate-800"
                  >
                    {t('backend:onboarding_logo_title')}
                    <Icon className="ml-1">
                      {charity.logo_url ? <Check /> : <ChevronRight/>}
                    </Icon>
                  </Link>
                </h4>
                <div className="text-slate-500">
                  {t('backend:onboarding_logo_description')}
                </div>
              </div>
            </div>
            <div className="flex space-x-4">
              <Badge>
                3
              </Badge>
              <div>
                <h4>
                  <Link
                    href={routes.embeds_page()}
                    className="text-slate-800"
                  >
                    {t('backend:onboarding_embeds_title')}
                    <Icon className="ml-1">
                      <ChevronRight />
                    </Icon>
                  </Link>
                </h4>
                <div className="text-slate-500">
                  {t('backend:onboarding_embeds_description')}
                </div>
              </div>
            </div>
            <div className="flex space-x-4 w-full">
              <Badge>
                4
              </Badge>
              <div className="w-full">
                <h4 className="text-slate-800">
                  {t('backend:onboarding_fundraising_title')}
                </h4>
                <div className="text-slate-500">
                  {t('backend:onboarding_fundraising_description', { charity: charity.title })}
                </div>
                <div className="flex space-x-2 w-full mt-2">
                  <Input
                    type="text"
                    value={charity.create_campaign_url}
                    readOnly
                    onFocus={(event) => event.target.select()}
                    inputSize="sm"
                  />
                  <Button
                    onClick={() => copyCreateCampaignUrl()}
                    variant="secondary"
                    className={classNames(copiedCreateCampaignUrl && '!text-slate-400')}
                    title={t('backend:copy_signup_link')}
                    size="sm"
                  >
                    <Icon className="mr-1">
                      {copiedCreateCampaignUrl ? <Check /> : <Copy />}
                    </Icon>
                    {t('backend:copy')}
                  </Button>
                  <AButton
                    href={charity.create_campaign_url}
                    target="_blank" rel="noopener noreferrer"
                    variant="secondary"
                    data-supporta-embed="false"
                    title={t('backend:open_signup_link')}
                    size="sm"
                  >
                    <Icon className="mr-1">
                      <ExternalLink />
                    </Icon>
                    {t('backend:open')}
                  </AButton>
                </div>
              </div>
            </div>
          </div>
          <div className="text-slate-500">
            {t('backend:onboarding_need_help')}
            {' '}
            <a href={`https://about.supporta.com/${locale}/contact`} target="_blank" rel="noopener noreferrer" data-supporta-embed="false">
              {t('backend:onboarding_contact_us')}
              <Icon className="ml-1">
                <ExternalLink />
              </Icon>
            </a>
          </div>
        </Card>
      </Container>
    </>
  );
}

function Badge({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <div className="flex items-center justify-center w-6 h-6 text-xs rounded-full text-emerald-700 bg-emerald-100">
      {children}
    </div>
  );
}

OnboardingPage.layout = (page: ReactNode) => <Layout>{page}</Layout>;
